import React, { useRef } from 'react'
import { TextInput } from '../../../../components/form/inputs/TextInput'
import { TextareaInput } from '../../../../components/form/inputs/TextareaInput'
import { DateInput } from '../../../../components/form/inputs/DateInput'
import { SelectInput } from '../../../../components/form/inputs/SelectInput'
import { RadioInput } from '../../../../components/form/inputs/RadioInput'
import { CheckboxInput } from '../../../../components/form/inputs/CheckboxInput'
import {
  Control,
  FieldErrors,
  Controller,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { FormField, FieldGroup } from '../../../../components/form'
import { PrefectureCategoryValues } from '../../../../enums/PrefectureCategory'
import { useImageUpload } from '../../../../hooks/use_image_upload'
import {
  IndustryCategoryParentValues,
  IndustryCategoryChildValues,
  IndustryCategoryCodeRelations,
} from '../../../../enums/IndustryCategory'
import {
  BusinessCategoryParentValues,
  BusinessCategoryChildValues,
  BusinessCategoryCodeRelations,
} from '../../../../enums/BusinessCategory'
import { EnterpriseCategoryValues } from '../../../../enums/EnterpriseCategory'
import { SDGsCategoryValues } from '../../../../enums/SDGsCategory'
import { EditFormData } from '../types'
import { FormErrorMessage } from '../../../../constants/message'
import noImage from '../../../../images/noimage.png'

interface Props {
  watch: UseFormWatch<EditFormData>
  setValue: UseFormSetValue<EditFormData>
  register: UseFormRegister<EditFormData>
  errors: FieldErrors<EditFormData>
  control: Control<EditFormData>
}

const KuchikomiEditTab: React.FC<Props> = ({
  watch,
  register,
  errors,
  control,
}) => {
  const logoFileInputRef = useRef<HTMLInputElement | null>(null)
  const { upload } = useImageUpload()

  // ロゴ画像アップロード処理
  const handleLogoImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const file = event.target.files?.[0]
    if (!file) return
    const response = await upload(file)
    if (response !== null) {
      onChange(response.url)
    }
  }

  return (
    <div className="space-y-8">
      <div className="border-b border-black pb-8 pt-6">
        <div className="space-y-4">
          <Controller
            control={control}
            name="kuchikomi.logoUrl"
            rules={{
              required: FormErrorMessage.required,
            }}
            render={({ field }) => (
              <>
                <FormField
                  label="ロゴ画像"
                  required
                  error={errors.kuchikomi?.logoUrl?.message}
                >
                  <div className="flex justify-start mb-2">
                    <div className="rounded-full w-fit">
                      <div
                        className="w-24 sm:w-32 h-24 sm:h-32 bg-cover rounded bg-center"
                        style={{
                          backgroundImage: `url(${field.value || noImage})`,
                        }}
                      ></div>
                    </div>
                    <div className="flex flex-col justify-center ml-4">
                      <label
                        htmlFor="logo-image-upload"
                        className="p-2 border border-black rounded-lg cursor-pointer"
                      >
                        画像をアップロード
                      </label>
                      <input
                        id="logo-image-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) =>
                          handleLogoImageUpload(e, field.onChange)
                        }
                        ref={logoFileInputRef}
                      />
                    </div>
                  </div>
                  <div className="text-sm">※データ形式はjpg、pngのみ対応</div>
                </FormField>
              </>
            )}
          ></Controller>

          <FormField
            label="企業・団体名"
            error={errors.kuchikomi?.name?.message}
            required
          >
            <TextInput
              register={register('kuchikomi.name', {
                required: FormErrorMessage.required,
                maxLength: {
                  value: 250,
                  message: FormErrorMessage.maxLength(250),
                },
              })}
            />
          </FormField>

          <FormField
            label="企業・団体説明"
            error={errors.kuchikomi?.description?.message}
          >
            <TextareaInput
              register={register('kuchikomi.description', {
                maxLength: {
                  value: 10000,
                  message: FormErrorMessage.maxLength(10000),
                },
              })}
              rows={4}
            />
          </FormField>

          <FieldGroup
            required
            fields={[
              {
                label: '代表者名前(姓)',
                children: (
                  <div>
                    <TextInput
                      register={register('kuchikomi.delegateLastName', {
                        required: FormErrorMessage.required,
                        maxLength: {
                          value: 50,
                          message: FormErrorMessage.maxLength(50),
                        },
                      })}
                    />
                    {errors.kuchikomi?.delegateLastName && (
                      <p className="error-message">
                        {errors.kuchikomi.delegateLastName.message}
                      </p>
                    )}
                  </div>
                ),
              },
              {
                label: '代表者名前(名)',
                children: (
                  <div>
                    <TextInput
                      register={register('kuchikomi.delegateFirstName', {
                        required: FormErrorMessage.required,
                        maxLength: {
                          value: 50,
                          message: FormErrorMessage.maxLength(50),
                        },
                      })}
                    />
                    {errors.kuchikomi?.delegateFirstName && (
                      <p className="error-message">
                        {errors.kuchikomi.delegateFirstName.message}
                      </p>
                    )}
                  </div>
                ),
              },
            ]}
          />

          <FormField
            label="代表者役職"
            error={errors.kuchikomi?.delegatePosition?.message}
            required
          >
            <TextInput
              register={register('kuchikomi.delegatePosition', {
                required: FormErrorMessage.required,
                maxLength: {
                  value: 50,
                  message: FormErrorMessage.maxLength(50),
                },
              })}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="担当者名"
            error={errors.kuchikomi?.responsiblePersonName?.message}
            required
          >
            <TextInput
              register={register('kuchikomi.responsiblePersonName', {
                required: FormErrorMessage.required,
                maxLength: {
                  value: 250,
                  message: FormErrorMessage.maxLength(250),
                },
              })}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="会社設立日"
            error={errors.kuchikomi?.establishDate?.message}
            required
          >
            <DateInput
              register={register('kuchikomi.establishDate', {
                required: FormErrorMessage.required,
              })}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="本社所在地(郵便番号)"
            error={errors.kuchikomi?.postCode?.message}
            required
          >
            <TextInput
              register={register('kuchikomi.postCode', {
                required: FormErrorMessage.required,
                minLength: {
                  value: 7,
                  message: FormErrorMessage.minLength(7),
                },
                maxLength: {
                  value: 7,
                  message: FormErrorMessage.maxLength(7),
                },
              })}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="本社所在地(都道府県)"
            error={errors.kuchikomi?.prefecture?.message}
            required
          >
            <SelectInput
              register={register('kuchikomi.prefecture', {
                required: FormErrorMessage.required,
              })}
              options={PrefectureCategoryValues.map((value) => ({
                value: value,
                label: value,
              }))}
              placeholder="選択してください"
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="本社所在地(市区町村以降)"
            error={errors.kuchikomi?.address?.message}
            required
          >
            <TextInput
              register={register('kuchikomi.address', {
                required: FormErrorMessage.required,
              })}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="代表電話番号"
            error={errors.kuchikomi?.tel?.message}
            required
          >
            <TextInput
              register={register('kuchikomi.tel', {
                required: FormErrorMessage.required,
                minLength: {
                  value: 11,
                  message: FormErrorMessage.minLength(11),
                },
                maxLength: {
                  value: 15,
                  message: FormErrorMessage.maxLength(15),
                },
              })}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="証券コード"
            error={errors.kuchikomi?.stockCode?.message}
          >
            <TextInput
              register={register('kuchikomi.stockCode')}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="資本金"
            error={errors.kuchikomi?.capitalPrice?.message}
          >
            <TextInput
              register={register('kuchikomi.capitalPrice')}
              type="number"
              unit="円"
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="社員数"
            error={errors.kuchikomi?.employeeNum?.message}
          >
            <TextInput
              register={register('kuchikomi.employeeNum')}
              type="number"
              unit="人"
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="公式サイトURL"
            error={errors.kuchikomi?.officialUrl?.message}
          >
            <TextInput
              register={register('kuchikomi.officialUrl', {
                maxLength: {
                  value: 250,
                  message: FormErrorMessage.maxLength(250),
                },
              })}
            />
          </FormField>

          <FormField
            label="X(旧Twitter)URL"
            error={errors.kuchikomi?.twitterUrl?.message}
          >
            <TextInput
              register={register('kuchikomi.twitterUrl', {
                maxLength: {
                  value: 250,
                  message: FormErrorMessage.maxLength(250),
                },
              })}
            />
          </FormField>

          <FormField
            label="Facebook URL"
            error={errors.kuchikomi?.facebookUrl?.message}
          >
            <TextInput
              register={register('kuchikomi.facebookUrl', {
                maxLength: {
                  value: 250,
                  message: FormErrorMessage.maxLength(250),
                },
              })}
            />
          </FormField>

          <FormField
            label="公式LINE URL"
            error={errors.kuchikomi?.lineUrl?.message}
          >
            <TextInput
              register={register('kuchikomi.lineUrl', {
                maxLength: {
                  value: 250,
                  message: FormErrorMessage.maxLength(250),
                },
              })}
            />
          </FormField>

          <FormField
            label="TikTok URL"
            error={errors.kuchikomi?.tiktokUrl?.message}
          >
            <TextInput
              register={register('kuchikomi.tiktokUrl', {
                maxLength: {
                  value: 250,
                  message: FormErrorMessage.maxLength(250),
                },
              })}
            />
          </FormField>

          <FieldGroup
            required
            fields={[
              {
                label: '業種カテゴリー(大)',
                children: (
                  <>
                    <SelectInput
                      register={register('kuchikomi.industryParentCode', {
                        required: FormErrorMessage.required,
                      })}
                      options={IndustryCategoryParentValues.map((item) => ({
                        value: item.code,
                        label: item.value,
                      }))}
                      placeholder="選択してください"
                    />
                    {errors.kuchikomi?.industryParentCode && (
                      <p className="error-message">
                        {errors.kuchikomi.industryParentCode.message}
                      </p>
                    )}
                  </>
                ),
              },
              {
                label: '業種カテゴリー(小)',
                children: (
                  <>
                    <SelectInput
                      register={register('kuchikomi.industryChildCode', {
                        required: FormErrorMessage.required,
                      })}
                      options={IndustryCategoryChildValues.filter((item) =>
                        IndustryCategoryCodeRelations[
                          watch('kuchikomi.industryParentCode')
                        ]?.includes(item.code)
                      ).map((item) => ({
                        value: item.code,
                        label: item.value,
                      }))}
                      placeholder="選択してください"
                    />
                    {errors.kuchikomi?.industryChildCode && (
                      <p className="error-message">
                        {errors.kuchikomi.industryChildCode.message}
                      </p>
                    )}
                  </>
                ),
              },
            ]}
          />

          <FormField
            label="企業・団体カテゴリー"
            error={errors.kuchikomi?.enterpriseCode?.message}
            required
          >
            <RadioInput
              name="kuchikomi.enterpriseCode"
              register={register('kuchikomi.enterpriseCode')}
              options={EnterpriseCategoryValues.map((item) => ({
                value: item.code,
                label: item.value,
              }))}
            />
          </FormField>

          <FieldGroup
            required
            fields={[
              {
                label: 'ビジネスカテゴリー(大)1',
                children: (
                  <>
                    <SelectInput
                      register={register('kuchikomi.businessCode1', {
                        required: FormErrorMessage.required,
                      })}
                      options={BusinessCategoryParentValues.map((item) => ({
                        value: item.code,
                        label: item.value,
                      }))}
                      placeholder="選択してください"
                    />
                    {errors.kuchikomi?.businessCode1 && (
                      <p className="error-message">
                        {errors.kuchikomi.businessCode1.message}
                      </p>
                    )}
                  </>
                ),
              },
              {
                label: 'ビジネスカテゴリー(小)1',
                children: (
                  <>
                    <SelectInput
                      register={register('kuchikomi.businessChildCode1', {
                        required: FormErrorMessage.required,
                      })}
                      options={BusinessCategoryChildValues.filter((item) =>
                        BusinessCategoryCodeRelations[
                          watch('kuchikomi.businessCode1')
                        ]?.includes(item.code)
                      ).map((item) => ({
                        value: item.code,
                        label: item.value,
                      }))}
                      placeholder="選択してください"
                    />
                    {errors.kuchikomi?.businessChildCode1 && (
                      <p className="error-message">
                        {errors.kuchikomi.businessChildCode1.message}
                      </p>
                    )}
                  </>
                ),
              },
            ]}
          />

          <FieldGroup
            fields={[
              {
                label: 'ビジネスカテゴリー(大)2',
                children: (
                  <>
                    <SelectInput
                      register={register('kuchikomi.businessCode2')}
                      options={BusinessCategoryParentValues.map((item) => ({
                        value: item.code,
                        label: item.value,
                      }))}
                      placeholder="選択してください"
                    />
                    {errors.kuchikomi?.businessCode2 && (
                      <p className="error-message">
                        {errors.kuchikomi.businessCode2.message}
                      </p>
                    )}
                  </>
                ),
              },
              {
                label: 'ビジネスカテゴリー(小)2',
                children: (
                  <>
                    <SelectInput
                      register={register('kuchikomi.businessChildCode2', {
                        required:
                          watch('kuchikomi.businessCode2') !== ''
                            ? 'ビジネスカテゴリー(大)2を選択した場合は必須です。'
                            : undefined,
                      })}
                      options={BusinessCategoryChildValues.filter((item) =>
                        BusinessCategoryCodeRelations[
                          watch('kuchikomi.businessCode2')
                        ]?.includes(item.code)
                      ).map((item) => ({
                        value: item.code,
                        label: item.value,
                      }))}
                      placeholder="選択してください"
                    />
                    {errors.kuchikomi?.businessChildCode2 && (
                      <p className="error-message">
                        {errors.kuchikomi.businessChildCode2.message}
                      </p>
                    )}
                  </>
                ),
              },
            ]}
          />

          <FieldGroup
            fields={[
              {
                label: 'ビジネスカテゴリー(大)3',
                children: (
                  <>
                    <SelectInput
                      register={register('kuchikomi.businessCode3')}
                      options={BusinessCategoryParentValues.map((item) => ({
                        value: item.code,
                        label: item.value,
                      }))}
                      placeholder="選択してください"
                    />
                    {errors.kuchikomi?.businessCode3 && (
                      <p className="error-message">
                        {errors.kuchikomi.businessCode3.message}
                      </p>
                    )}
                  </>
                ),
              },
              {
                label: 'ビジネスカテゴリー(小)3',
                children: (
                  <>
                    <SelectInput
                      register={register('kuchikomi.businessChildCode3', {
                        required:
                          watch('kuchikomi.businessCode3') !== ''
                            ? 'ビジネスカテゴリー(大)3を選択した場合は必須です。'
                            : undefined,
                      })}
                      options={BusinessCategoryChildValues.filter((item) =>
                        BusinessCategoryCodeRelations[
                          watch('kuchikomi.businessCode3')
                        ]?.includes(item.code)
                      ).map((item) => ({
                        value: item.code,
                        label: item.value,
                      }))}
                      placeholder="選択してください"
                    />
                    {errors.kuchikomi?.businessChildCode3 && (
                      <p className="error-message">
                        {errors.kuchikomi.businessChildCode3.message}
                      </p>
                    )}
                  </>
                ),
              },
            ]}
          />

          <FormField
            label="SDGsカテゴリー"
            error={errors.kuchikomi?.interestingSDGs?.message}
            required
          >
            <CheckboxInput
              name="sdgs"
              register={register('kuchikomi.interestingSDGs', {
                validate: (value) => {
                  if (!value || value.length === 0) {
                    return FormErrorMessage.atLeastOne
                  }
                  return true
                },
              })}
              options={SDGsCategoryValues.map((item) => ({
                value: item.code,
                label: item.code + '. ' + item.value,
              }))}
              layout="vertical"
            />
          </FormField>
        </div>
      </div>
    </div>
  )
}

export default KuchikomiEditTab
